import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import API from '@/config/api';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import {
  ArrowRight, TrendingUp, Crown, CheckCircle,
  GraduationCap, Sparkles, ExternalLink, BookMarked,
  ChevronRight, Shield, Star
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_legacy-trader/artifacts/6c7ukjms_Copy%20of%20Copy%20of%20NRL%20Graphic%20Logo%201.png";
const BOOK_COVER = "https://customer-assets.emergentagent.com/job_3a854a85-d099-4060-868a-517959e97093/artifacts/c7l5gfu4_7289C4F8-554B-401A-9251-05B028DA18D5.png";
const HERO_BG = "https://images.unsplash.com/photo-1709377195538-5522ed0f9e10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHx3ZWFsdGglMjBhYnN0cmFjdCUyMGRhcmt8ZW58MHx8fHwxNzgyODU4MjA1fDA&ixlib=rb-4.1.0&q=85";
const CONSULT_BG = "https://images.unsplash.com/photo-1699621106755-4fe40ce95d64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBvZmZpY2UlMjBtb2Rlcm58ZW58MHx8fHwxNzgyODU4MjA1fDA&ixlib=rb-4.1.0&q=85";

const heading = "font-['Cormorant_Garamond',serif]";
const body = "font-['Outfit',sans-serif]";

const membershipTiers = [
  {
    name: "Legacy Explorer",
    price: "$0",
    period: "forever",
    features: [
      "Paper Trading Access",
      "Gilded Study (Free Library)",
      "Portfolio Overview & Trading Journal",
      "Lead Generator (Affiliate Portal)",
      "AI Assistant Chatbot",
      "Free Financial Consultations"
    ],
    cta: "Start Free",
    popular: false,
    tradingAccess: false,
    packageId: "free",
    requiresPayment: false,
    externalLink: null
  },
  {
    name: "Wealth Architect",
    price: "$497",
    period: "/mo",
    annualPrice: "$4,997/yr",
    features: [
      "Everything in Legacy Explorer",
      "NRL University Courses",
      "Community Access",
      "Email Support"
    ],
    cta: "Enroll Now",
    popular: true,
    tradingAccess: false,
    packageId: "wealth_architect_monthly",
    requiresPayment: true,
    externalLink: "https://nrluniversity.com/courses"
  },
  {
    name: "Dynasty Builder",
    price: "$1,497",
    period: "/mo",
    annualPrice: "$14,997/yr",
    features: [
      "Everything in Wealth Architect",
      "LIVE Trading Access",
      "1-on-1 Coaching (Biweekly)",
      "Full Self-Paced Course Library",
      "Priority Support & VIP Community"
    ],
    cta: "Build Your Dynasty",
    popular: false,
    tradingAccess: true,
    packageId: "dynasty_builder_monthly",
    requiresPayment: true,
    externalLink: "https://nrluniversity.com/courses"
  }
];

const services = [
  {
    title: "Legacy Blueprint",
    price: "FREE",
    duration: "30 min",
    items: ["Personal finance Q&A", "Resource recommendations", "Clear action steps"],
    type: "financial"
  },
  {
    title: "Wealth Strategy",
    price: "$297",
    duration: "1 hour",
    items: ["Business strategy analysis", "Wealth optimization plan", "7-day follow-up"],
    type: "business"
  },
  {
    title: "Empire Building",
    price: "$750",
    duration: "2 hours",
    items: ["VIP mentorship session", "Scaling blueprint", "14-day accountability"],
    type: "business"
  },
  {
    title: "Dynasty Advisory",
    price: "$2,500",
    duration: "Half-day",
    items: ["Executive transformation", "Full business audit", "30-day support"],
    type: "business"
  }
];

const editions = [
  { id: "wealth_lexicon_ebook", label: "eBook", price: "$39" },
  { id: "wealth_lexicon_softback", label: "Softback", price: "$75" },
  { id: "wealth_lexicon_hardback", label: "Hardback", price: "$150", popular: true },
  { id: "wealth_lexicon_vip", label: "VIP Bundle", price: "$250" },
];

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [selectedEdition, setSelectedEdition] = useState("wealth_lexicon_hardback");

  const handleCheckout = async (packageId, packageType) => {
    if (!isAuthenticated) {
      sessionStorage.setItem('pendingUpgrade', packageId);
      navigate('/login');
      return;
    }
    try {
      const res = await fetch(`${API}/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ package_id: packageId, package_type: packageType, origin_url: window.location.origin })
      });
      const data = await res.json();
      if (data.success && data.checkout_url) window.location.href = data.checkout_url;
      else alert(data.detail || 'Checkout failed');
    } catch { alert('Something went wrong. Please try again.'); }
  };

  return (
    <div className={`min-h-screen bg-[#0a0a0f] ${body} text-[#a39ba3]`} data-testid="landing-page">
      <SEO {...SEO_CONFIG.home} />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/70 to-[#0a0a0f]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f]/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-40 w-full">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-8">
              <img src={LOGO_URL} alt="NRL" className="w-12 h-12 object-contain" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Nouveau Riche Legacy</span>
            </div>

            <h1 className={`${heading} text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#e9dcc9] leading-[1.1] mb-6`}>
              Legacy Is Built,<br />
              <span className="text-[#D4AF37]">Not Inherited.</span>
            </h1>

            <p className="text-lg sm:text-xl text-[#a39ba3] leading-relaxed mb-10 max-w-lg">
              Financial education, trading tools, and wealth-building resources — 
              delivered through courses, coaching, and community.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/login')}
                className="bg-[#D4AF37] text-[#0a0a0f] font-semibold tracking-wide hover:bg-[#f2c94c] text-base px-8 py-6 transition-all duration-300"
                data-testid="hero-cta-primary"
              >
                {isAuthenticated ? 'Go to Dashboard' : 'Join NRL University'} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                onClick={() => document.getElementById('lexicon').scrollIntoView({ behavior: 'smooth' })}
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 text-base px-8 py-6 transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                Explore The Lexicon
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-10">
          <div className="w-5 h-9 border border-[#D4AF37]/40 rounded-full flex justify-center pt-2">
            <div className="w-0.5 h-2 bg-[#D4AF37]/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* ═══════════════ THE WEALTH LEXICON ═══════════════ */}
      <section id="lexicon" className="py-24 md:py-32 px-6 md:px-12 bg-[#14120f] relative overflow-hidden" data-testid="wealth-lexicon-section">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] border border-[#D4AF37]/5 rounded-full translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] border border-[#D4AF37]/5 rounded-full -translate-x-1/3 translate-y-1/3" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 md:gap-16 items-center">
            {/* Book Cover */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative group">
                <div className="absolute -inset-8 bg-[#D4AF37]/8 blur-[60px] rounded-full group-hover:bg-[#D4AF37]/12 transition-all duration-700" />
                <img
                  src={BOOK_COVER}
                  alt="The Wealth Lexicon"
                  className="relative z-10 w-64 sm:w-72 lg:w-80 rounded-lg shadow-2xl shadow-black/60 group-hover:scale-[1.02] transition-transform duration-500"
                  data-testid="book-cover-image"
                />
              </div>
            </div>

            {/* Purchase Options */}
            <div className="lg:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Pre-Order Now</p>
              <h2 className={`${heading} text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#e9dcc9] mb-3`}>
                The Wealth <span className="text-[#D4AF37]">Lexicon</span>
              </h2>
              <p className={`${heading} text-lg text-[#e9dcc9]/80 italic mb-6`}>Don't Let Them Speak Circles Around You</p>
              <p className="text-base text-[#a39ba3] mb-8 max-w-md leading-relaxed">
                Your essential guide to financial literacy. Master the language of money, markets, and generational wealth.
              </p>

              {/* Edition Selector */}
              <div className="space-y-3 mb-8">
                {editions.map((ed) => (
                  <button
                    key={ed.id}
                    onClick={() => setSelectedEdition(ed.id)}
                    className={`w-full flex items-center justify-between px-5 py-4 rounded-lg border transition-all duration-300 text-left
                      ${selectedEdition === ed.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 shadow-lg shadow-[#D4AF37]/5'
                        : 'border-[#2d2722] bg-[#1c1814] hover:border-[#D4AF37]/40'
                      }`}
                    data-testid={`edition-select-${ed.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors
                        ${selectedEdition === ed.id ? 'border-[#D4AF37]' : 'border-[#2d2722]'}`}>
                        {selectedEdition === ed.id && <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
                      </div>
                      <span className={`font-medium ${selectedEdition === ed.id ? 'text-[#e9dcc9]' : 'text-[#a39ba3]'}`}>
                        {ed.label}
                      </span>
                      {ed.popular && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full">
                          Popular
                        </span>
                      )}
                    </div>
                    <span className={`text-lg font-semibold ${selectedEdition === ed.id ? 'text-[#D4AF37]' : 'text-[#a39ba3]'}`}>
                      {ed.price}
                    </span>
                  </button>
                ))}
              </div>

              <Link to="/the-wealth-lexicon" data-testid="wealth-lexicon-cta">
                <Button className="bg-[#D4AF37] text-[#0a0a0f] font-semibold tracking-wide hover:bg-[#f2c94c] px-10 py-6 text-base transition-all duration-300 w-full sm:w-auto">
                  <BookMarked className="h-4 w-4 mr-2" /> Pre-Order Now <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ MEMBERSHIP TIERS ═══════════════ */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0f]" id="pricing" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Membership Tiers</p>
            <h2 className={`${heading} text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#e9dcc9] mb-4`}>
              NRL <span className="text-[#D4AF37]">University</span>
            </h2>
            <p className="text-base text-[#a39ba3] max-w-lg mx-auto leading-relaxed">
              Choose your path. Courses delivered at{' '}
              <a href="https://nrluniversity.com" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline underline-offset-4 decoration-[#D4AF37]/40 hover:decoration-[#D4AF37] transition-colors">
                nrluniversity.com
              </a>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {membershipTiers.map((tier, i) => (
              <div
                key={i}
                className={`relative rounded-xl border p-8 md:p-10 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/50
                  ${tier.popular
                    ? 'border-[#D4AF37] bg-[#1c1814] shadow-xl shadow-[#D4AF37]/5'
                    : 'border-[#2d2722] bg-[#1c1814] hover:border-[#D4AF37]/50'
                  }`}
                data-testid={`tier-card-${tier.packageId}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#D4AF37] text-[#0a0a0f] text-[10px] font-bold uppercase tracking-[0.15em] px-4 py-1.5 rounded-full flex items-center gap-1">
                      <Crown className="h-3 w-3" /> Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6 pb-6 border-b border-[#2d2722]">
                  <h3 className={`${heading} text-2xl font-medium text-[#e9dcc9] mb-3`}>{tier.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className={`${heading} text-5xl font-light text-[#D4AF37]`}>{tier.price}</span>
                    <span className="text-sm text-[#a39ba3]">{tier.period}</span>
                  </div>
                  {tier.annualPrice && (
                    <p className="text-xs text-[#a39ba3]/60 mt-2">or {tier.annualPrice} (save 2 months)</p>
                  )}
                  <div className="mt-3">
                    {tier.tradingAccess ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full">
                        <TrendingUp className="h-3 w-3" /> Live Trading
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-blue-400 bg-blue-500/10 border border-blue-500/20 px-3 py-1 rounded-full">
                        <TrendingUp className="h-3 w-3" /> Paper Trading
                      </span>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-[#a39ba3]">
                      <CheckCircle className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {tier.externalLink && (
                  <a
                    href={tier.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 text-xs text-[#D4AF37]/70 hover:text-[#D4AF37] mb-4 transition-colors"
                    data-testid={`nrl-link-${tier.packageId}`}
                  >
                    <GraduationCap className="h-3.5 w-3.5" />
                    View courses at NRL University
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}

                <Button
                  onClick={() => {
                    if (!tier.requiresPayment) {
                      isAuthenticated ? navigate('/dashboard') : navigate('/login');
                      return;
                    }
                    handleCheckout(tier.packageId, 'membership');
                  }}
                  className={`w-full py-6 font-semibold tracking-wide transition-all duration-300
                    ${tier.popular
                      ? 'bg-[#D4AF37] text-[#0a0a0f] hover:bg-[#f2c94c]'
                      : 'bg-transparent border border-[#2d2722] text-[#e9dcc9] hover:border-[#D4AF37] hover:text-[#D4AF37]'
                    }`}
                  data-testid={`tier-cta-${tier.packageId}`}
                >
                  {tier.cta} <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ CONSULTING SERVICES ═══════════════ */}
      <section className="py-24 md:py-32 px-6 md:px-12 relative overflow-hidden" id="services" data-testid="consulting-section">
        <div className="absolute inset-0">
          <img src={CONSULT_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0a0a0f]/90" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">1-on-1 Sessions</p>
            <h2 className={`${heading} text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#e9dcc9] mb-4`}>
              Consulting <span className="text-[#D4AF37]">Services</span>
            </h2>
            <p className="text-base text-[#a39ba3] max-w-md mx-auto leading-relaxed">
              Personal guidance from wealth experts. Your first call is <span className="text-emerald-400 font-semibold">free</span>.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <div
                key={i}
                className={`rounded-xl border p-6 md:p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/50
                  ${s.type === 'financial'
                    ? 'border-emerald-500/30 bg-[#1c1814]/90 hover:border-emerald-500/60 backdrop-blur-sm'
                    : 'border-[#2d2722] bg-[#1c1814]/90 hover:border-[#D4AF37]/50 backdrop-blur-sm'
                  }`}
                data-testid={`consult-card-${i}`}
              >
                {s.type === 'financial' && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full w-fit mb-4">
                    <Sparkles className="h-3 w-3" /> Free
                  </span>
                )}

                <h3 className={`${heading} text-xl font-medium text-[#e9dcc9] mb-1`}>{s.title}</h3>
                <div className="flex items-baseline gap-2 mb-5">
                  <span className={`text-2xl font-semibold ${s.type === 'financial' ? 'text-emerald-400' : 'text-[#D4AF37]'}`}>{s.price}</span>
                  <span className="text-xs text-[#a39ba3]">{s.duration}</span>
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {s.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#a39ba3]">
                      <CheckCircle className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${s.type === 'financial' ? 'text-emerald-400' : 'text-[#D4AF37]'}`} />
                      {item}
                    </li>
                  ))}
                </ul>

                <Link to="/booking">
                  <Button
                    className={`w-full font-semibold tracking-wide transition-all duration-300
                      ${s.type === 'financial'
                        ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                        : 'bg-[#D4AF37] hover:bg-[#f2c94c] text-[#0a0a0f]'
                      }`}
                    data-testid={`consult-cta-${i}`}
                  >
                    {s.type === 'financial' ? 'Book Free Call' : 'Book Now'}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#1c1814]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className={`${heading} text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#e9dcc9] mb-5`}>
            Your Dynasty <span className="text-[#D4AF37]">Awaits.</span>
          </h2>
          <p className="text-base text-[#a39ba3] mb-10 max-w-md mx-auto leading-relaxed">
            Start as a Legacy Explorer for free. Upgrade when you're ready to build.
          </p>
          <Button
            onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/login')}
            className="bg-[#D4AF37] text-[#0a0a0f] font-semibold tracking-wide hover:bg-[#f2c94c] text-lg px-12 py-7 transition-all duration-300"
            data-testid="final-cta"
          >
            {isAuthenticated ? 'Go to Dashboard' : 'Get Started'} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="py-16 md:py-24 px-6 md:px-12 bg-[#0a0a0f] border-t border-[#2d2722]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div>
              <img src={LOGO_URL} alt="NRL" className="w-12 h-12 mb-4 opacity-80" />
              <p className="text-sm text-[#a39ba3]/60 leading-relaxed">
                Building generational wealth through education and community.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[#e9dcc9] mb-4">Platform</h4>
              <ul className="space-y-2.5 text-sm text-[#a39ba3]/70">
                <li><Link to="/trading" className="hover:text-[#D4AF37] transition-colors">Trading Terminal</Link></li>
                <li><Link to="/gilded-study" className="hover:text-[#D4AF37] transition-colors">The Gilded Study</Link></li>
                <li><Link to="/ai-assistant" className="hover:text-[#D4AF37] transition-colors">AI Assistant</Link></li>
                <li><Link to="/the-wealth-lexicon" className="hover:text-[#D4AF37] transition-colors">The Wealth Lexicon</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[#e9dcc9] mb-4">Company</h4>
              <ul className="space-y-2.5 text-sm text-[#a39ba3]/70">
                <li><Link to="/about" className="hover:text-[#D4AF37] transition-colors">About</Link></li>
                <li><Link to="/booking" className="hover:text-[#D4AF37] transition-colors">Book Consultation</Link></li>
                <li><a href="https://nrluniversity.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">NRL University</a></li>
                <li><Link to="/web3-services" className="hover:text-[#D4AF37] transition-colors">Web3 Services</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[#e9dcc9] mb-4">Legal</h4>
              <ul className="space-y-2.5 text-sm text-[#a39ba3]/70">
                <li><Link to="/terms" className="hover:text-[#D4AF37] transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-[#D4AF37] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/disclaimer" className="hover:text-[#D4AF37] transition-colors">Risk Disclaimer</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#2d2722] pt-8 text-center text-xs text-[#a39ba3]/40">
            <p>&copy; {new Date().getFullYear()} Nouveau Riche Legacy. All rights reserved.</p>
            <p className="mt-1">Educational content only. Not financial advice.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
